// Stage 5 offline copies: one IndexedDB record bound to the signed-in account. Every write is a read-modify-write inside ONE
// IndexedDB transaction (serialized across tabs) and is refused when the account or wipe epoch changed since the caller started,
// so stale tabs and late network responses can never overwrite newer edits or resurrect data after sign-out. Credentials are never stored here.
const DB = "af-offline", STORE = "kv", KEY = "state", EPOCH = "af-offline-epoch", OWNER = "af-offline-owner";
const STATIC = /^\/(static|fonts|images)\//;
const bc = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("af-offline") : null;
try { performance.setResourceTimingBufferSize(2000); } catch { /* older browsers */ }

// Only a well-formed, unexpired token with a user id counts as signed in.
export const uidFromToken = (token = localStorage.getItem("token")) => {
  try {
    const p = String(token || "").split(".");
    if (p.length !== 3) return null;
    const b = p[1].replace(/-/g, "+").replace(/_/g, "/");
    const j = JSON.parse(atob(b + "===".slice((b.length + 3) % 4)));
    if (typeof j.user_id !== "string" || !j.user_id || typeof j.exp !== "number" || j.exp * 1000 <= Date.now()) return null;
    return j.user_id;
  } catch { return null; }
};

const epoch = () => Number(localStorage.getItem(EPOCH) || 0);
export const offlineCtx = () => ({ uid: uidFromToken(), epoch: epoch() });
export const isCurrent = (ctx) => live(ctx);
const live = (ctx) => !!ctx.uid && uidFromToken() === ctx.uid && epoch() === ctx.epoch;

export class StaleError extends Error {
  constructor() { super("This device's offline copy changed (signed out, account switched or cleared). That result was ignored."); this.code = "stale"; }
}

const open = () => new Promise((res, rej) => {
  if (!window.indexedDB) { rej(new Error("This browser can't store offline copies.")); return; }
  const r = indexedDB.open(DB, 1);
  r.onupgradeneeded = () => r.result.createObjectStore(STORE);
  r.onsuccess = () => res(r.result);
  r.onerror = () => rej(r.error);
});
const mapErr = (e) => (e?.name === "QuotaExceededError" ? new Error("Your device is out of storage space for offline copies. Remove some items or free up space.") : e || new Error("Couldn't save offline copy."));

// One transaction: read the CURRENT stored state, check account/epoch, apply fn, write. fn returns the next state, or undefined for no change.
export async function updateOffline(ctx, fn) {
  const db = await open();
  return new Promise((res, rej) => {
    let out, err;
    const t = db.transaction(STORE, "readwrite"), s = t.objectStore(STORE), g = s.get(KEY);
    g.onsuccess = () => {
      try {
        const cur = g.result || null;
        if (!live(ctx) || (cur && cur.owner !== ctx.uid)) throw new StaleError();
        const next = fn(cur);
        if (next === undefined) { out = cur; return; }
        next.ver = (cur?.ver || 0) + 1;
        s.put(next, KEY);
        localStorage.setItem(OWNER, next.owner);
        out = next;
      } catch (e) { err = e; try { t.abort(); } catch { /* done */ } }
    };
    t.oncomplete = () => { db.close(); if (out !== g.result) bc?.postMessage({ type: "changed" }); res(out); };
    t.onerror = t.onabort = () => { db.close(); rej(err || mapErr(t.error)); };
  });
}

const read = async () => {
  const db = await open();
  return new Promise((res, rej) => {
    const g = db.transaction(STORE, "readonly").objectStore(STORE).get(KEY);
    g.onsuccess = () => { db.close(); res(g.result || null); };
    g.onerror = () => { db.close(); rej(g.error); };
  });
};

export async function wipeOffline() {
  try { localStorage.setItem(EPOCH, String(epoch() + 1)); localStorage.removeItem(OWNER); } catch { /* ignore */ }
  try { const db = await open(); await new Promise((r) => { const t = db.transaction(STORE, "readwrite"); t.objectStore(STORE).delete(KEY); t.oncomplete = t.onerror = t.onabort = () => { db.close(); r(); }; }); } catch { /* nothing stored */ }
  bc?.postMessage({ type: "wiped" });
}

// Returns the state only if it belongs to the account signed in right now; otherwise wipes it.
export async function loadOffline() {
  const uid = uidFromToken();
  const st = await read().catch(() => null);
  if (!st) return null;
  if (!uid || st.owner !== uid) { await wipeOffline(); return null; }
  return st;
}

// Calls cb whenever another tab changed or wiped the offline copy, or the sign-in changed.
export function onOfflineChange(cb) {
  const onMsg = (e) => cb(e.data?.type);
  const onStorage = (e) => { if (e.key === EPOCH || e.key === "token" || e.key === null) cb("storage"); };
  bc?.addEventListener("message", onMsg);
  window.addEventListener("storage", onStorage);
  return () => { bc?.removeEventListener("message", onMsg); window.removeEventListener("storage", onStorage); };
}

// Other tabs: a sign-out or account switch elsewhere wipes the copy here too.
export function installOfflineGuards() {
  const onStorage = (e) => {
    if (e.key !== "token" && e.key !== null) return;
    const owner = localStorage.getItem(OWNER);
    if (owner && uidFromToken() !== owner) wipeOffline().then(() => { if (window.location.pathname === "/offline") window.location.reload(); });
  };
  window.addEventListener("storage", onStorage);
  const owner = localStorage.getItem(OWNER);
  if (owner && uidFromToken() !== owner) wipeOffline();
  return () => window.removeEventListener("storage", onStorage);
}

const pathOf = (u) => { try { const p = new URL(u, window.location.origin); return p.origin === window.location.origin && STATIC.test(p.pathname) ? p.pathname : null; } catch { return null; } };

// Caches the /offline page and the same-origin static files it needs, and reports ready ONLY after the service worker
// acknowledged every required file and each one is readable from the cache.
export async function prepareShell() {
  if (!("serviceWorker" in navigator) || !window.caches) return { ok: false, reason: "This browser can't open pages without a connection." };
  try {
    const reg = await navigator.serviceWorker.register("/offline-sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    const sw = reg.active || navigator.serviceWorker.controller;
    if (!sw) return { ok: false, reason: "The offline helper didn't start." };
    const req = new Set(["/offline"]), opt = new Set();
    [...[...document.scripts].map(s => s.src), ...[...document.querySelectorAll('link[rel="stylesheet"]')].map(l => l.href), ...performance.getEntriesByType("resource").map(e => e.name)]
      .forEach(u => { const p = pathOf(u); if (p) (/\.(js|css)$/.test(p) ? req : opt).add(p); });
    const ack = await new Promise((resolve) => {
      const ch = new MessageChannel(), timer = setTimeout(() => resolve(null), 45000);
      ch.port1.onmessage = (e) => { clearTimeout(timer); resolve(e.data); };
      sw.postMessage({ type: "warm", urls: [...req, ...opt] }, [ch.port2]);
    });
    if (!ack) return { ok: false, reason: "Preparing the offline page timed out." };
    const missing = (await Promise.all([...req].map(async u => (ack.ok.includes(u) && (await caches.match(u)) ? null : u)))).filter(Boolean);
    return missing.length ? { ok: false, reason: `${missing.length} of ${req.size} required files couldn't be stored.` } : { ok: true, files: req.size };
  } catch { return { ok: false, reason: "The offline helper couldn't be set up." }; }
}
